import React, { useMemo } from "react";
import type { MenuProps } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import IconDropdownButton from "./IconDropdownButton";
import { divider, group, item } from "./menu";
import { IconFont } from "../../../component/icon-font";

type Device = { label: string; deviceId: string };

type Props = {
  disabled?: boolean;
  isStartedVideo: boolean;
  cameraList: Device[];
  activeCameraId?: string;

  isMirrored?: boolean;
  isBlur?: boolean;
  showMirror?: boolean;
  showBlur?: boolean;

  // NEW
  selfRotateDeg?: 0 | 90 | 180 | 270;
  showRotateSelf?: boolean;
  onRotateSelf?: (deg: 0 | 90 | 180 | 270) => void;

  onToggle: () => void;
  onSelectCamera: (deviceId: string) => void;
  onToggleMirror?: () => void;
  onToggleBlur?: () => void;
};

export default function CameraControl({
  disabled,
  isStartedVideo,
  cameraList,
  activeCameraId,
  isMirrored,
  isBlur,
  showMirror = true,
  showBlur = true,

  selfRotateDeg = 0,
  showRotateSelf = true,
  onRotateSelf,

  onToggle,
  onSelectCamera,
  onToggleMirror,
  onToggleBlur,
}: Props) {
  const tooltip = isStartedVideo ? "Stop camera" : "Start camera";

  const icon = useMemo(() => {
    return isStartedVideo ? (
      <IconFont type="icon-participant-video-on" />
    ) : (
      <IconFont type="icon-participant-video-off" />
    );
  }, [isStartedVideo]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [];

    // Mirror / Blur
    if (showMirror && onToggleMirror) {
      items.push(item("Mirror my video", "mirror", isMirrored ? <CheckOutlined /> : undefined));
    }
    if (showBlur && onToggleBlur) {
      items.push(item("Blur background", "blur", isBlur ? <CheckOutlined /> : undefined));
    }

    // Rotate self (submenu)
    if (showRotateSelf && onRotateSelf) {
      const rotateChildren = ([
        { label: "0° (normal)", deg: 0 },
        { label: "90°", deg: 90 },
        { label: "180°", deg: 180 },
        { label: "270°", deg: 270 },
      ] as const).map((r) =>
        item(
          r.label,
          `rotate|${r.deg}`,
          selfRotateDeg === r.deg ? <CheckOutlined /> : undefined
        )
      );

      items.push(
        group("Rotate self", rotateChildren)
      );
    }

    if (
      (showMirror && onToggleMirror) ||
      (showBlur && onToggleBlur) ||
      (showRotateSelf && onRotateSelf)
    ) {
      items.push(divider);
    }

    // Cameras
    if (cameraList?.length) {
      items.push(
        group(
          "Select a camera",
          cameraList.map((d) =>
            item(
              d.label || d.deviceId,
              `camera|${d.deviceId}`,
              d.deviceId === activeCameraId ? <CheckOutlined /> : undefined
            )
          )
        )
      );
    }

    return items.length ? items : undefined;
  }, [
    cameraList,
    activeCameraId,
    showMirror,
    showBlur,
    onToggleMirror,
    onToggleBlur,
    isMirrored,
    isBlur,
    showRotateSelf,
    onRotateSelf,
    selfRotateDeg,
  ]);

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    const k = String(key);
    if (k === "mirror") return onToggleMirror?.();
    if (k === "blur") return onToggleBlur?.();

    if (k.startsWith("rotate|")) {
      const deg = Number(k.split("|")[1]) as 0 | 90 | 180 | 270;
      if ([0, 90, 180, 270].includes(deg)) onRotateSelf?.(deg);
      return;
    }

    if (k.startsWith("camera|")) return onSelectCamera(k.split("|")[1]);
  };

  return (
    <IconDropdownButton
      tooltip={tooltip}
      icon={icon}
      onClick={onToggle}
      menuItems={menuItems}
      onMenuClick={onMenuClick}
      disabled={disabled}
    />
  );
}
